import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useAppContext } from 'context/AppContext';
import { useForm } from 'react-final-form';
import { makeStyles } from 'styles/util';
import { getStorageGroupAndStorageTitle } from 'helpers/StorageSiteHelper';
import { formatIsoDate } from 'helpers/DateHelper';
import rentalTypes from 'enums/rentalTypes';
import paymentRecurrences from 'enums/paymentRecurrences';
import { createTimePeriodPricesBasedOnStorageGroup } from 'helpers/PriceHelper';
import { createTimePeriodPriceReferenceDictionary, createFormDataForTimePeriodPrice, createTimePeriodPrice } from 'logic/timePeriodPriceLogic';
import bookingStatuses from 'enums/bookingStatuses';
import { sub } from 'date-fns';
import { minimumDateString } from 'logic/bookingLogic';

import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import TimePeriodPricesEditor from 'form/TimePeriodPricesEditor';
import StorageSelectorDialog from './StorageSelectorDialog';
import LinkButton from 'common/LinkButton';

const useStyles = makeStyles(({ theme, colors }) => ({
    container: {
        border: `2px solid ${colors.lightGreyBorder}`,
        borderRadius: '6px',
        padding: theme.spacing(2, 2, 0, 2),
        marginBottom: theme.spacing(2),
        '& div, & label, & input, & p': {
            fontSize: '0.75rem'
        }
    },
    storageTitleContainer: {
        margin: theme.spacing(2, 0),
        '& button': {
            fontSize: '1rem',
        }
    },
    showStorageSelectorDialogButtonContainer: {
        flex: '0 0 auto'
    },
    notAvailable: {
        color: colors.textColorDisabled
    }
}));

const StorageEditor = ({ booking, storageSite }) => {
    const classes = useStyles();
    const form = useForm();
    const formValues = form.getState().values;
    const { appContext } = useAppContext();
    const [storageSelectorDialogOpen, setStorageSelectorDialogOpen] = useState(false);

    const selectedStorageGroup = storageSite.storageGroups
        .find(storageGroup => storageGroup.id === formValues.storageGroupId);

    const availableStorageIds = storageSite.bookingCalendar.storageGroupBookingCalendars
        .find(sgbc => sgbc.storageGroupId === selectedStorageGroup.id)
        .storageBookingCalendars.filter(sbc => sbc.availableBookingIntervals.length === 1 && sbc.availableBookingIntervals[0].startDate === formatIsoDate(new Date(), appContext))
        .map(sbc => sbc.storageId);
    const selectedStorage = selectedStorageGroup.storages
        .find(storage => storage.id === formValues.storageId);
    const selectedStorageIsAvailable = selectedStorage && availableStorageIds.includes(selectedStorage.id);
    const selectedStorageTitle = <>{getStorageGroupAndStorageTitle(selectedStorage, selectedStorageGroup, appContext)} {selectedStorageIsAvailable === false && '- not available'}</>;

    const isCustomArea = selectedStorageGroup.rentalType === rentalTypes.customArea.key;

    const canEditStorage = [bookingStatuses.requested.key, bookingStatuses.confirmed.key].some(o => o === booking.bookingStatus);

    const handleShowStorageSelectorDialogButtonClick = () => {
        setStorageSelectorDialogOpen(true);
    };

    const handleStorageSelectorDialogOk = values => {
        if(values.storageGroupId !== formValues.storageGroupId && booking.subscriptionBooking) {
            // storage group changed
            const newStorageGroup = storageSite.storageGroups.find(storageGroup => storageGroup.id === values.storageGroupId);
            // update the time period prices
            let updatedTimePeriodPrices;
            if(booking.bookingStatus === bookingStatuses.purchased.key) {
                // keep time period prices for old storage group up until yesterday
                // add time period prices for new storage group from today and on
                const today = formatIsoDate(new Date(), appContext);
                const yesterday = formatIsoDate(sub(new Date(), { days: 1 }), appContext);
                const oldTimePeriodPrices = formValues.storageBookingItem.timePeriodPrices
                    .map(tpp => ({ ...tpp, ...createTimePeriodPrice(tpp, appContext) }))
                    .filter(tpp => (formatIsoDate(tpp.startDate, appContext) ?? minimumDateString) < today);
                const newTimePeriodPrices = createTimePeriodPricesBasedOnStorageGroup(booking, storageSite, newStorageGroup, appContext)
                    .map(tpp => ({
                        ...tpp,
                        editable: true,
                        endDateEditable: true,
                        inUse: false
                    }));
                oldTimePeriodPrices[oldTimePeriodPrices.length - 1].endDate = yesterday;
                newTimePeriodPrices[0].startDate = today;
                updatedTimePeriodPrices = oldTimePeriodPrices.concat(newTimePeriodPrices);
            } else {
                // use time period prices for new storage group
                updatedTimePeriodPrices = createTimePeriodPricesBasedOnStorageGroup(booking, storageSite, newStorageGroup, appContext)
                    .map(tpp => ({
                        ...tpp,
                        editable: true,
                        endDateEditable: true,
                        inUse: false
                    }));
            }
            const formDataForUpdatedTimePeriodPrices = updatedTimePeriodPrices.map(tpp => createFormDataForTimePeriodPrice(tpp, booking.currency, appContext));
            form.mutators.changeValue({ name: 'storageBookingItem.timePeriodPrices', value: formDataForUpdatedTimePeriodPrices });
        }
        form.mutators.changeValue({ name: 'storageGroupId', value: values.storageGroupId });
        form.mutators.changeValue({ name: 'storageId', value: values.storageId });
        setStorageSelectorDialogOpen(false);
    };

    const handleStorageSelectorDialogCancel = () => {
        setStorageSelectorDialogOpen(false);
    };

    return (
        <Box className={classes.container}>
            <Typography variant="body1" gutterBottom>
                Storage
            </Typography>
            {
                !isCustomArea &&
                (
                    <Box className={classes.storageTitleContainer}>
                        {
                            canEditStorage &&
                            (
                                <LinkButton onClick={handleShowStorageSelectorDialogButtonClick}>
                                    {selectedStorageTitle}
                                </LinkButton>
                            )
                        }
                        {
                            !canEditStorage && selectedStorageTitle
                        }
                        <StorageSelectorDialog
                            storageSite={storageSite}
                            open={storageSelectorDialogOpen}
                            onOk={handleStorageSelectorDialogOk}
                            onCancel={handleStorageSelectorDialogCancel}
                        />
                    </Box>
                )
            }

            <TimePeriodPricesEditor
                fieldNamePrefix="storageBookingItem.timePeriodPrices"
                amountLabel={paymentRecurrences.perMonth.title}
                currency={booking.currency}
                referenceDictionary={createTimePeriodPriceReferenceDictionary(storageSite)}
                vatRateVisible
                commissionRateVisible
                timePeriodVisible
                linkIconVisible
            />
        </Box>
    );
};

StorageEditor.propTypes = {
    booking: PropTypes.object.isRequired,
    storageSite: PropTypes.object.isRequired
};

export default StorageEditor;
